import classNames from "classnames";
import { t } from "@/i18n-js/instance";
import { NewEventButton } from "@/react/components/Events/NewEventButton";
import { OpenQPModal } from "@/react/components/QuickPost/OpenQPModal";
import { SortOptions } from "@/react/components/Spaces/Header/SpaceHeaderTitleDropdown/spaceOptions/SortOptions";
import { NewImagePostButton } from "@/react/components/Spaces/ImageSpace/NewImagePostButton";
import { useAiSummariesEnabled } from "@/react/components/Spaces/hooks/useAiSummariesEnabled";
import {
  canManageSpace,
  isBasicPostsSpace,
  isChatSpace,
  isEventsSpace,
  isImagesSpace,
  isSortingEnabled,
} from "@/react/helpers/spaceHelpers";
import type { SpaceDateRange } from "@/react/types/AiSummary/SpaceSummary";
import type { Space, SpaceViewType } from "@circle-react/types";
import { useShouldHideMembersCount } from "./AvatarsButton/useShouldHideMembersCount";
import { ChatRoomMessageSearchButton } from "./ChatRoomMessageSearchButton";
import { EventSpaceActions } from "./EventSpaceActions";
import { ManageSpaceActionsV3 } from "./ManageSpaceActionsV3";
import { PostSpaceActions } from "./PostSpaceActions";
import { SummarizeDropdown } from "./SummarizeDropdown";
import { ToggleRailbarButton } from "./ToggleRailbarButton";

export interface SpaceMemberActionsProps {
  space: Space;
  shouldShowChatRailbar: boolean;
  toggleChatRailbar: () => void;
  onSummarizeClick: (range: SpaceDateRange) => void;
  onViewTypeChange?: (viewType: SpaceViewType) => void;
  activeViewType?: SpaceViewType;
}

export const SpaceMemberActions = ({
  space,
  shouldShowChatRailbar,
  toggleChatRailbar,
  onSummarizeClick,
}: SpaceMemberActionsProps) => {
  const isSpaceEventSpace = isEventsSpace(space);
  const isSpacePostSpace = isBasicPostsSpace(space);
  const isSpaceChatSpace = isChatSpace(space);
  const isSpaceImagesSpace = isImagesSpace(space);
  const canMemberManageSpace = canManageSpace(space);

  const canMemberCreatePosts =
    space?.policies?.can_create_post || canMemberManageSpace;
  const hasAiSpacesSummariesEnabled = useAiSummariesEnabled();

  const shouldHideMembersCount = useShouldHideMembersCount(space);

  return (
    <div className="flex items-center">
      <div
        className={classNames("flex items-center", {
          "gap-6": !isSpaceChatSpace,
          "gap-2": isSpaceChatSpace,
        })}
      >
        {isSortingEnabled(space) && <SortOptions space={space} />}
        {isSpacePostSpace && hasAiSpacesSummariesEnabled && (
          <SummarizeDropdown onClick={onSummarizeClick} />
        )}
        {(isSpacePostSpace || isSpaceImagesSpace) &&
          !shouldHideMembersCount && (
            <div className="hidden items-center gap-5 md:flex">
              <PostSpaceActions space={space} />
            </div>
          )}
        {isSpaceEventSpace && (
          <div className="hidden items-center gap-6 md:flex">
            <EventSpaceActions space={space} />
          </div>
        )}
        {isSpaceChatSpace && (
          <>
            <ChatRoomMessageSearchButton />
            <ToggleRailbarButton
              shouldShowChatRailbar={shouldShowChatRailbar}
              toggleChatRailbar={toggleChatRailbar}
            />
          </>
        )}
        {canMemberCreatePosts && isSpacePostSpace && (
          <OpenQPModal linkName={t("new_post")} className="hidden md:flex" />
        )}
        {canMemberCreatePosts && isSpaceImagesSpace && (
          <NewImagePostButton spaceId={space.id} />
        )}
        {isSpaceEventSpace && canMemberCreatePosts && (
          <div className="hidden md:flex">
            <NewEventButton />
          </div>
        )}
        <ManageSpaceActionsV3 />
      </div>
    </div>
  );
};
